import React, { useEffect } from "react";
import { css } from "glamor";
import { Helmet } from "react-helmet";
import "./styles.css";

// Native <details> (the "Sommaire" box, class "encadre-summary", and the FAQ
// items, class "encadre") toggle their [open] attribute synchronously on
// click, so a plain CSS transition never gets a "before" frame to animate
// from and the content just snaps open/closed, shoving everything below it
// around instantly. Animating the <details> element's own height with the
// Web Animations API sidesteps that: it plays its own keyframes regardless
// of how the attribute flip was applied, and works whether the content is a
// single <ul> (Sommaire) or several <p> tags (FAQ).
const animateDetailsOpen = () => {
  document
    .querySelectorAll("details.encadre-summary, details.encadre")
    .forEach((details) => {
      if (details.dataset.smoothDetailsBound) return;
      details.dataset.smoothDetailsBound = "true";

      const summary = details.querySelector(":scope > summary");
      if (!summary) return;

      // Set once, permanently: keeping overflow consistent between the
      // collapsed/expanded measurements and the steady state before/after
      // each animation avoids a margin-collapsing mismatch that otherwise
      // causes a small height "settle" jump right as the animation hands
      // off to CSS.
      details.style.overflow = "hidden";

      // These boxes use the default content-box sizing, so the `height`
      // property excludes padding/border — measured (border-box) heights
      // have to be trimmed down to content height or the box ends up
      // padding taller than intended while the animation is running.
      const measureContentHeight = () => {
        const boxStyle = getComputedStyle(details);
        const boxAdjust =
          boxStyle.boxSizing === "border-box"
            ? 0
            : parseFloat(boxStyle.paddingTop) +
              parseFloat(boxStyle.paddingBottom) +
              parseFloat(boxStyle.borderTopWidth) +
              parseFloat(boxStyle.borderBottomWidth);
        return details.getBoundingClientRect().height - boxAdjust;
      };

      let isAnimating = false;

      summary.addEventListener("click", (event) => {
        if (isAnimating) return;
        event.preventDefault();

        let fromHeight;
        let toHeight;
        let onDone;

        if (!details.open) {
          fromHeight = measureContentHeight();
          details.open = true;
          toHeight = measureContentHeight();
          onDone = () => {
            details.style.height = "";
          };
        } else {
          fromHeight = measureContentHeight();
          // Toggling closed then back open (synchronously, before the next
          // paint) reveals the true collapsed height without ever showing
          // the collapsed frame on screen, so the closing animation can
          // start from the real expanded height and land exactly on it.
          details.open = false;
          toHeight = measureContentHeight();
          details.open = true;
          onDone = () => {
            details.style.height = "";
            details.open = false;
          };
        }

        isAnimating = true;
        const animation = details.animate(
          [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
          { duration: 350, easing: "ease" }
        );
        animation.onfinish = () => {
          isAnimating = false;
          onDone();
        };
      });
    });
};

css.global("*", { padding: "0" });
css.global("*", { margin: "0" });

css.global("html, body", { color: "#1f1f1f" });
css.global("html, body", {
  font:
    "400 16px/26px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
});

css.global("strong, b", {
  font:
    "600 16px/26px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
});

css.global("h1", {
  fontFamily:
    "'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
});

css.global("h2", {
  font:
    "500 26px/35px fontFamily: 'SF Pro Display', 'SF Pro Text', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
});

css.global("h2", {
  color: "#3c4d68",
});

css.global("h2", {
  margin: ".9em 0 .4em",
  lineHeight: "1.3em",
});

css.global("h2", {
  letterSpacing: ".02em",
});

css.global("h3", {
  font:
    "500 19px/27px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
});

css.global("h3", {
  color: "#3c4d68",
});

css.global("h3", {
  margin: ".5em 0 .4em",
});

css.global("h3", {
  letterSpacing: ".01em",
});

css.global("h4", {
  marginBottom: ".5em",
});

css.global("html, body", {
  WebkitFontSmoothing: "antialiased",
});

css.global("html, body", {
  MozOsxFontSmoothing: "grayscale",
});

css.global("a, p a, ul li a", { textDecoration: "none" });
css.global("p a:focus, ul li a:focus", { outline: "none" });
css.global("p a:focus, ul li a:focus", { opacity: ".9" });
css.global("p", { margin: "0 0 .9em" });
css.global("p a, ul li a", { color: "#2c2e31" });
css.global("p a, ul li a", { padding: ".08em" });
css.global("p a, ul li a", { borderBottom: "1px solid #2c2e31" });
css.global("p a, ul li a", { transition: "all .3s" });
css.global("p a:hover, ul li a:hover", { textDecoration: "none" });
css.global("p a:hover, ul li a:hover", {
  backgroundColor: "hsla(193,5%,62%,.2)",
});
css.global("p a:hover, ul li a:hover", { borderColor: "hsla(193,5%,62%,.2)" });
css.global("p a:hover, ul li a:hover", { transition: "all .3s" });

css.global("ul", { margin: "0 0 .8em 1.6em !important" });
css.global("ol", { margin: "0 0 .8em 2em !important" });

css.global("::placeholder", { color: "white !important" });

css.global("input::focus", { outline: "none !important" });

css.global(".intro", {
  font:
    "400 21px/31px -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif, 'Apple Color Emoji', 'Segoe UI Emoji', 'Segoe UI Symbol'",
});

css.insert(
  ".videoWrapper{position:relative;padding-bottom:56.25%;padding-top:25px;height:0;}.videoWrapper iframe{position:absolute;top:0;left:0;width:100%;height:100%;}@media(max-width:768px) {.intro {font-size:19px;line-height:28px;}.responsive-image{width: 100% !important; margin-top: 0 !important;}h2{font-size: 25px ; line-height:32px; letter-spacing: .01em;}ul{margin: 0 0 .1em 1em !important;}}"
);

css.global("body-contouring", { margin: "0" });

css.global(".align-right", { textAlign: "right" });

export default ({ children }) => {
  useEffect(() => {
    animateDetailsOpen();
  });

  return (
    <div>
      <Helmet>
      <script type="application/ld+json">{`
{
  "@context": "http://schema.org",
  "@type": "Person",
  "name": "Dr Cédric Kron",
  "url": "https://www.dr-kron.com/",
  "sameAs": [
    "https://www.facebook.com/DrCedricKron",
    "https://www.instagram.com/docteurkron/",
    "https://twitter.com/Dr_Kron",
    "https://www.youtube.com/channel/UCCbRUwoTEbXraAeMVIN9DuA",
    "https://www.linkedin.com/in/kron-c%C3%A9dric-28492917",
    "https://plus.google.com/+DocteurKron"
  ]
}
`}</script>
      <script type="application/ld+json">{`
{
  "@context": "http://schema.org",
  "@type": "Organization",
  "url": "https://www.dr-kron.com",
  "contactPoint": [
    { "@type": "ContactPoint",
      "telephone": "+33-1-45-62-85-00",
      "contactType": "reservations"
    }
  ]
}
`}</script>
      <link rel="preconnect" href="https://www.dr-kron.com" />
      <script
        type="module"
        src="https://static.cloudflareinsights.com/beacon.min.js"
        data-cf-beacon='{"token": "2a5490dc440542eba75515f1a36b292e"}'
      ></script>
    </Helmet>

      {children}
    </div>
  );
};

css.global(".sommaire", {
  border: "1px solid rgba(73,105,126,0.08)",
  boxShadow: "0 1px 7px rgba(0,0,0,.05)",
  borderRadius: `5px`,
  backgroundColor: `white`,
  padding: `.8em 1em .5em 1em`,
  display: `flex`,
});

css.global(".sommaire-titre", {
  display: `inline-block`,
  width: `20%`,
  color: `#3c4d68`,
  letterSpacing: `.03em`,
  textTransform: `uppercase`,
  fontSize: `.9em`,
  fontWeight: `600`,
  margin: `0 0 .6em`,
  padding: `0`,
});

css.global(".sommaire ul", {
  display: `inline-block`,
  width: `80%`,
  listStyleType: `none`,
  margin: `0 !important`,
  padding: `0`,
});

css.global(".sommaire ul li", {
  margin: `0 0 .5em`,
  fontSize: `.9em`,
  lineHeight: `1.5em`,
});

css.global(".sommaire ul li::before", {
  marginRight: `5px`,
});

css.global(".sommaire a", {
  borderBottom: `none`,
});

css.insert(
  "@media(max-width:768px) { .sommaire { display: block; } .sommaire-titre { display: block; width: 100%;} .sommaire ul {display: block; width: 100%;}}"
);

css.global("table", {
  border: `1px solid rgba(73,105,126,0.08)`,
  boxShadow: `0 1px 7px rgba(0,0,0,.05)`,
  borderRadius: `4px`,
  backgroundColor: `white`,
  padding: `.5em .22em .7em`,
  fontSize: `.88em`,
  lineHeight: `1.63em`,
  tableLayout: `fixed`,
  wordWrap: `break-word`,
});

css.global("table p", {
  fontSize: `1em`,
  lineHeight: `1.63em`,
});

css.global("table strong", {
  fontSize: `1em`,
  lineHeight: `1.63em`,
});

css.global("table th", {
  color: `#49697e`,
  fontSize: `1.5em`,
  fontWeight: `600`,
  padding: `.5em`,
});

css.global("table td", {
  padding: `.45em .35em`,
  verticalAlign: `top`,
});

css.global("table td.subtitle", {
  fontSize: `1.25em`,
  fontWeight: `600`,
  color: `#49697e`,
});

css.global("table td.link", {
  textAlign: `center`,
  fontWeight: `600`,
});

css.global("table td.link a", {
  color: `#49697e !important`,
});

css.global(".legende", {
  textAlign: `center`,
  fontSize: `0.87em`,
  paddingTop: `-8px`,
  lineHeight: `1.5em`,
  fontStyle: `italic`,
});
